import React, { useEffect, useRef, useState } from 'react';
import { CartItem } from '../CartItem/CartItem';
import { BillSummary } from '../BillSummary/BillSummary';
import { CustomerLookup } from '../CustomerLookup/CustomerLookup';
import { Customer360Dashboard } from '../../SharedDomainComponents/Customer360Dashboard/Customer360Dashboard';
import { BillModeSelector } from '../BillModeSelector/BillModeSelector';
import { BILL_MODE_META } from '../BillModeSelector/types';
import { Button } from '../../BasicComponents/Button/Button';
import { Toggle } from '../../BasicComponents/Toggle/Toggle';
import { formatMoney } from '../../../utils/currency';
import type { CartSidebarProps } from './types';
import {
  HIGHLIGHT_DURATION_MS,
  highlightClasses,
  prefersReducedMotion,
  scrollBehaviorFor,
  shouldScrollToItem,
} from './cart-highlight';

// ─── Credit account (L02) ─────────────────────────────────────────────────────

function CreditAccountCard({
  creditLimit,
  outstanding,
  asOf,
  action,
  fmt,
}: NonNullable<CartSidebarProps['creditAccount']> & { fmt: (minor: number) => string }) {
  const noLimit = creditLimit <= 0;
  const available = noLimit ? null : Math.max(0, creditLimit - outstanding);
  const atLimit = !noLimit && outstanding >= creditLimit;
  return (
    <section
      aria-label="Credit account"
      data-testid="cart-credit-account"
      className={[
        'mt-2 rounded-lg border px-3 py-2 shadow-sm animate-fade-in transition-all duration-200',
        atLimit
          ? 'border-warning-300 bg-warning-50 dark:border-warning-700 dark:bg-warning-900/20'
          : 'border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 dark:border-indigo-800 dark:from-indigo-950/40 dark:to-violet-950/40',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={[
            'text-xs font-semibold uppercase tracking-wide',
            atLimit ? 'text-warning-700 dark:text-warning-300' : 'text-indigo-700 dark:text-indigo-300',
          ].join(' ')}
        >
          Credit account
        </span>
        {action}
      </div>
      <dl className="mt-1 grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Owes</dt>
          <dd
            data-testid="cart-credit-owes"
            className={[
              'font-semibold tabular-nums',
              atLimit ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-white',
            ].join(' ')}
          >
            {fmt(outstanding)}
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Limit</dt>
          <dd data-testid="cart-credit-limit" className="font-semibold tabular-nums text-neutral-900 dark:text-white">
            {noLimit ? 'No limit' : fmt(creditLimit)}
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Available</dt>
          <dd
            data-testid="cart-credit-available"
            className={[
              'font-semibold tabular-nums',
              atLimit ? 'text-warning-700 dark:text-warning-300' : 'text-success-700 dark:text-success-300',
            ].join(' ')}
          >
            {available === null ? '—' : fmt(available)}
          </dd>
        </div>
      </dl>
      {asOf && (
        <p data-testid="cart-credit-as-of" className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
          Last known · {asOf}
        </p>
      )}
    </section>
  );
}

// ─── CartSidebar ──────────────────────────────────────────────────────────────

export function CartSidebar({
  items,
  highlightItemId,
  onQuantityChange,
  onDiscountChange,
  onRemove,
  selectedCustomer,
  customer360,
  creditAccount,
  onCustomerSelect,
  onCustomerClear,
  onCustomerSearch,
  billMode,
  onBillModeChange,
  subtotal,
  discountAmount,
  billDiscountAmount,
  cgst,
  sgst,
  igst,
  taxSlabs,
  roundOff,
  total,
  exchangeCredit,
  quoteAdvance,
  pendingDiscountApproval,
  onCheckout,
  storeCreditAvailable = 0,
  membershipInfo,
  modePermissions,
  lockedModes,
  currency = 'INR',
  taxInclusive = false,
  isInterState = false,
  onInterStateChange,
  interStateWarning,
  taxNote,
  className,
}: CartSidebarProps) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  const isEmpty = items.length === 0;
  const modeMeta = BILL_MODE_META[billMode];
  // Modes that move no money show the exact total: with the round-off row
  // hidden, a rounded total would disagree with the saved document (QA 4.6.2).
  const shownTotal = modeMeta.movesMoneyOnCheckout ? total : total - roundOff;
  // An exchange's return credit and a quote's down payment settle part of the
  // bill; the CTA charges the rest (SS2-005).
  const payable = shownTotal - (exchangeCredit?.amount ?? 0) - (quoteAdvance?.amount ?? 0);
  const showCreditBanner =
    storeCreditAvailable > 0 && !isEmpty && modeMeta.movesMoneyOnCheckout;
  const creditRedeemable = Math.min(storeCreditAvailable, total);

  // ── "Just added" scroll + flash ──────────────────────────────────────────
  // Keyed off `highlightItemId` (not the items array) so only a real add or
  // quantity increase moves the scroll — editing, removing or decrementing a
  // line leaves the cashier where they were.
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [flashedItemId, setFlashedItemId] = useState<string | null>(null);
  // Read through a ref so the effect can check membership without taking
  // `items` as a dependency (which would re-scroll on every cart edit).
  const itemIdsRef = useRef<string[]>([]);
  itemIdsRef.current = items.map((i) => i.id);

  useEffect(() => {
    if (!shouldScrollToItem(highlightItemId, itemIdsRef.current)) return;
    const id = highlightItemId as string;

    rowRefs.current[id]?.scrollIntoView(scrollBehaviorFor(prefersReducedMotion()));
    setFlashedItemId(id);

    const timer = setTimeout(() => setFlashedItemId(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
    // Deliberately keyed on `highlightItemId` alone. Including `items` would
    // re-run on every cart mutation and re-scroll while the cashier is editing
    // an unrelated line.
  }, [highlightItemId]);

  return (
    <aside
      role="complementary"
      aria-label="Shopping cart"
      className={[
        'flex flex-col h-full bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-700',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header: bill mode */}
      <div className="px-4 pt-4 pb-2 border-b border-neutral-100 dark:border-neutral-700 space-y-3 shrink-0">
        <BillModeSelector
          value={billMode}
          onChange={onBillModeChange}
          permissions={modePermissions}
          lockedModes={lockedModes}
        />

        {/* Mode helper banner — surfaced only for non-sale modes so cashiers
            don't forget mid-transaction that the cart's intent has changed. */}
        {modeMeta.bannerHelper && (
          <div
            role="status"
            aria-live="polite"
            className={[
              'rounded-md px-3 py-2 text-xs font-medium',
              modeMeta.bannerTint,
            ].join(' ')}
          >
            {modeMeta.bannerHelper}
          </div>
        )}

        <CustomerLookup
          selectedCustomer={selectedCustomer}
          onSelect={onCustomerSelect}
          onClear={onCustomerClear}
          onSearch={onCustomerSearch}
          currency={currency}
        />

        {/*
          The customer's history, at the counter.

          BELOW the lookup chip rather than replacing it: the chip owns the
          clear button, and a cashier who has just attached the wrong person
          needs that more than they need the numbers.

          Rendered only once the profile has actually loaded — an empty card
          with dashes where a lifetime spend should be tells the cashier
          nothing and costs vertical space on the busiest screen in the
          product.
        */}
        {selectedCustomer && customer360 && (
          <Customer360Dashboard
            variant="compact"
            currency={currency}
            className="mt-2"
            {...customer360}
          />
        )}

        {/*
          L02: what a credit customer owes, as soon as they are picked — not
          only after the cashier opens Account Credit in the payment window.
          Kept apart from the Customer 360 card, whose "store credit" is money
          the shop owes the customer, the other way round.
        */}
        {selectedCustomer && creditAccount && (
          <CreditAccountCard {...creditAccount} fmt={fmt} />
        )}
      </div>

      {/* Cart items */}
      <div className="flex-1 overflow-y-auto py-2">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-10 text-neutral-400 dark:text-neutral-500">
            <svg className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 001.95-1.51L23 6H6" />
            </svg>
            <p className="text-sm font-medium">Scan or search to add products</p>
          </div>
        ) : (
          <div>
            {items.map((item) => (
              // Wrapper carries the scroll target and the flash tint. Keeping
              // the tint on the row (not the container) leaves the summary and
              // totals below untouched.
              <div
                key={item.id}
                ref={(el) => { rowRefs.current[item.id] = el; }}
                className={[
                  'rounded-lg transition-colors duration-500',
                  highlightClasses(flashedItemId === item.id),
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <CartItem
                  {...item}
                  onQuantityChange={onQuantityChange}
                  onDiscountChange={onDiscountChange}
                  onRemove={onRemove}
                  currency={currency}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bill summary + checkout */}
      {!isEmpty && (
        <div className="border-t border-neutral-200 dark:border-neutral-600 px-4 py-3 space-y-3 shrink-0">
          {/* Store-credit availability — informational banner above the
              summary. The actual redemption happens in the PaymentModal
              (Store Credit tile). Hidden for non-money modes (quote/credit)
              since they don't settle now. */}
          {showCreditBanner && (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md bg-info-50 dark:bg-info-900/20 px-3 py-2 text-xs font-medium text-info-800 dark:text-info-200 flex items-baseline justify-between gap-3"
            >
              <span>
                🎟️ {fmt(storeCreditAvailable)} store credit available
              </span>
              <span className="font-mono tabular-nums text-info-700 dark:text-info-300">
                redeem up to {fmt(creditRedeemable)}
              </span>
            </div>
          )}
          {/* Membership tier discount — the amount is already folded into the
              Discount row below; this banner attributes it so the cashier and
              customer see why the bill dropped. */}
          {membershipInfo && (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs font-medium text-amber-800 dark:text-amber-200 flex items-baseline justify-between gap-3"
            >
              <span>
                ⭐ {membershipInfo.tier} member · {membershipInfo.planName}
              </span>
              <span className="font-mono tabular-nums text-amber-700 dark:text-amber-300">
                {membershipInfo.discountPct}% off
              </span>
            </div>
          )}
          {onInterStateChange && (
            <Toggle
              size="sm"
              checked={isInterState}
              onChange={onInterStateChange}
              label="Inter-state supply (IGST)"
            />
          )}
          {onInterStateChange && interStateWarning && (
            <p
              role="note"
              className="rounded-md bg-warning-50 dark:bg-warning-900/20 px-3 py-1.5 text-xs font-medium text-warning-700 dark:text-warning-300"
            >
              {interStateWarning}
            </p>
          )}
          <BillSummary
            subtotal={subtotal}
            discountAmount={discountAmount}
            billDiscountAmount={billDiscountAmount}
            cgst={cgst}
            sgst={sgst}
            igst={igst}
            taxSlabs={taxSlabs}
            // Round-off is a settlement artifact. Hide it for modes where no
            // money is captured at checkout — quote/credit conversions will
            // recalc it at the moment money actually moves.
            roundOff={modeMeta.movesMoneyOnCheckout ? roundOff : 0}
            total={shownTotal}
            pendingDiscountApproval={pendingDiscountApproval}
            currency={currency}
            taxInclusive={taxInclusive}
            taxNote={taxNote}
          />
          {exchangeCredit && (
            <div className="flex justify-between text-sm text-success-700 dark:text-success-300">
              <span>Exchange credit ({exchangeCredit.creditNoteNumber})</span>
              <span className="font-medium tabular-nums">-{fmt(exchangeCredit.amount)}</span>
            </div>
          )}
          {quoteAdvance && (
            <div className="flex justify-between text-sm text-success-700 dark:text-success-300">
              <span>Down payment ({quoteAdvance.quoteNumber})</span>
              <span className="font-medium tabular-nums whitespace-nowrap">-{fmt(quoteAdvance.amount)}</span>
            </div>
          )}
          <Button
            variant={modeMeta.ctaVariant}
            size="lg"
            fullWidth
            onClick={onCheckout}
            aria-label={
              modeMeta.movesMoneyOnCheckout
                ? `${modeMeta.ctaLabel(fmt(payable))}`
                : `${modeMeta.ctaLabel(fmt(payable))} for ${fmt(payable)}`
            }
          >
            {modeMeta.ctaLabel(fmt(payable))}
          </Button>
        </div>
      )}
    </aside>
  );
}
